import { createColumnHelper } from "@tanstack/react-table";

import { FiUser } from "react-icons/fi";

import CrudActions from "components/crud/CrudActions";

const columnHelper = createColumnHelper();

export const employeeColumns = ({ onEdit, onDelete }) => [
  // =========================================================
  // EMPLOYEE CODE
  // =========================================================

  columnHelper.accessor("employeeCode", {
    id: "employeeCode",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Employee Code
      </span>
    ),

    cell: (info) => (
      <p className="font-semibold text-white-700">
        {info.getValue() || "-"}
      </p>
    ),
  }),

  // =========================================================
  // NAME
  // =========================================================

  columnHelper.accessor((row) => row.user?.name, {
    id: "name",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Name
      </span>
    ),

    cell: (info) => (
      <div className="flex items-center gap-2">
        <div
          className="
            flex
            h-9
            w-9
            items-center
            justify-center
            rounded-full
            bg-brand-500
            text-white
          "
        >
          <FiUser size={18} />
        </div>

        <div>
          <p className="font-semibold text-white-700">
            {info.getValue() || "-"}
          </p>
        </div>
      </div>
    ),
  }),

  // =========================================================
  // EMAIL
  // =========================================================

  columnHelper.accessor((row) => row.user?.email, {
    id: "email",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Email
      </span>
    ),

    cell: (info) => (
      <span>
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // PHONE
  // =========================================================

  columnHelper.accessor((row) => row.user?.phone, {
    id: "phone",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Phone
      </span>
    ),

    cell: (info) => (
      <span>
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // DESIGNATION
  // =========================================================

  columnHelper.accessor("designation", {
    id: "designation",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Designation
      </span>
    ),

    cell: (info) => (
      <span>
        {info.getValue() || "-"}
      </span>
    ),
  }),

  // =========================================================
  // ROLE
  // =========================================================

  columnHelper.accessor("roles", {
    id: "roles",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Role
      </span>
    ),

    cell: (info) => {
      const roles = info.getValue();

      return (
        <span>
          {roles?.length
            ? roles
                .map((role) => role.name)
                .join(", ")
            : "-"}
        </span>
      );
    },
  }),

  // =========================================================
  // STATUS
  // =========================================================

  columnHelper.accessor("status", {
    id: "status",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Status
      </span>
    ),

    cell: (info) => {
      const status = info.getValue();

      const statusClass =
        status === "active"
          ? "bg-green-50 text-green-700 border-green-200"
          : status === "suspended"
          ? "bg-yellow-50 text-yellow-700 border-yellow-200"
          : status === "transferred"
          ? "bg-blue-50 text-blue-700 border-blue-200"
          : "bg-red-50 text-red-700 border-red-200";

      return (
        <span
          className={`
            inline-flex
            rounded-full
            border
            px-3
            py-1
            text-xs
            font-semibold
            capitalize
            ${statusClass}
          `}
        >
          {status || "-"}
        </span>
      );
    },
  }),

  // =========================================================
  // JOINING DATE
  // =========================================================

  columnHelper.accessor("joiningDate", {
    id: "joiningDate",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Joining Date
      </span>
    ),

    cell: (info) => {
      const value = info.getValue();

      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleDateString("en-IN");
    },
  }),

  // =========================================================
  // LEAVING DATE
  // =========================================================

  columnHelper.accessor("leavingDate", {
    id: "leavingDate",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Leaving Date
      </span>
    ),

    cell: (info) => {
      const value = info.getValue();

      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleDateString("en-IN");
    },
  }),

  // =========================================================
  // ACTIONS
  // =========================================================

  columnHelper.display({
    id: "actions",

    header: () => (
      <span className="text-xs font-bold uppercase text-gray-500">
        Actions
      </span>
    ),

    cell: ({ row }) => {
      const employee = row.original;

      return (
        <CrudActions
          onEdit={
            onEdit
              ? () => onEdit(employee)
              : undefined
          }

          onDelete={
            onDelete
              ? () => onDelete(employee.id)
              : undefined
          }

          showEdit={
            typeof onEdit === "function"
          }

          showDelete={
            typeof onDelete === "function"
          }

          editTitle="Edit Employee"
          deleteTitle="Delete Employee"
        />
      );
    },
  }),
];